import { useEffect, useState } from "react";
import { Save, CheckSquare } from "lucide-react";
import { getPermissions, assignPermissions } from "../api/permissionApi";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
} from "./ui";

const PermissionSelector = ({
  roleId,
  selectedPermissions = [],
  refreshRole,
}) => {
  const [permissions, setPermissions] = useState([]);
  const [checked, setChecked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPermissions();
  }, []);

  useEffect(() => {
    setChecked(selectedPermissions.map((permission) => permission._id));
  }, [selectedPermissions]);

  const loadPermissions = async () => {
    try {
      const data = await getPermissions();
      setPermissions(data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const togglePermission = (id) => {
    if (checked.includes(id)) {
      setChecked(checked.filter((item) => item !== id));
    } else {
      setChecked([...checked, id]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await assignPermissions(roleId, checked);
      alert("Permissions updated");
      refreshRole();
    } catch (error) {
      alert("Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
      {/* Header – no decorative icon */}
      <CardHeader className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-4 sm:px-6 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle className="text-xs font-semibold text-neutral-800 dark:text-neutral-100 uppercase tracking-wider">
            Role Permissions ({checked.length}/{permissions.length})
          </CardTitle>
          <p className="mt-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">
            Configure access privileges and action capabilities for this role.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="text-[10px] font-semibold">
            {checked.length} selected
          </Badge>

          <Button
            type="button"
            variant="primary"
            isLoading={saving}
            leftIcon={<Save className="h-3.5 w-3.5" />}
            onClick={handleSave}
            className="text-xs font-semibold px-4 py-2 w-full sm:w-auto"
          >
            Save Changes
          </Button>
        </div>
      </CardHeader>

      {/* Grid Content */}
      <CardContent className="p-4 sm:p-6">
        {permissions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckSquare className="h-8 w-8 text-neutral-300 dark:text-neutral-600 mb-2" />
            <p className="text-xs text-neutral-400 dark:text-neutral-500">
              No permissions available to configure.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {permissions.map((permission) => {
              const isChecked = checked.includes(permission._id);
              return (
                <label
                  key={permission._id}
                  className="flex items-center justify-between p-3.5 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors cursor-pointer select-none"
                >
                  <div className="min-w-0 pr-3 flex-1">
                    <p className="text-xs font-medium text-neutral-800 dark:text-neutral-100 tracking-tight truncate">
                      {permission.name}
                    </p>
                    {permission.description && (
                      <p className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                        {permission.description}
                      </p>
                    )}
                  </div>

                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => togglePermission(permission._id)}
                    className="sr-only"
                  />

                  {/* Minimal Toggle Switch */}
                  <div
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${
                      isChecked
                        ? "bg-primary-600 dark:bg-primary-500"
                        : "bg-neutral-200 dark:bg-neutral-700"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out my-auto ml-0.5 ${
                        isChecked ? "translate-x-4" : "translate-x-0"
                      }`}
                    />
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PermissionSelector;
